import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CartPaymentMethodContainer from '../../Cart/CartPaymentMethod/CartPaymentMethodContainer';
import {
  PrimaryButtonHeadline,
  PrimaryButton,
} from '../../../components/Buttons';
import { Formik } from 'formik';
import appConfig from '../../../../app-config';
import { Headline } from '../../../components/Typography';
import ConfirmDelete from '../../PaymentMethods/components/ConfirmDelete';
import TextField from '@material-ui/core/TextField';

const Wrapper = styled.div`
  display: grid;
  grid-gap: 24px;
`;
const CardDetailsWrapper = styled.div`
  display: grid;
  grid-gap: 10px;
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 10px;
`;

const TitleWrapper = styled.p`
  color: #000000;
  text-transform: capitalize;
  margin-bottom: auto;
  font-size: 16px;
  font-family: ${(props) => props.theme.secondaryFontFontFamily};
`;

const FundWrapper = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 20px;
  margin-bottom: 30px;
`;

const MakeDefaultButton = styled.div`
  padding: 19px 16px 19px 24px;
  font-family: ${(props) => props.theme.primaryFontFontFamily};
  border: 2px solid
    ${(props) =>
      props.disabled
        ? props.theme.connectExistingGiftCardButtonInactiveBorderColor
        : props.theme.connectExistingGiftCardButtonBorderColor};
  border-radius: ${(props) => props.theme.giftCardButtonRadius};
  background-color: ${(props) =>
    props.theme.connectExistingGiftCardButtonBackgroundColor};
  color: ${(props) =>
    props.disabled
      ? props.theme.gray
      : props.theme.connectExistingGiftCardButtonTextColor};
  cursor: ${(props) => (props.disabled ? 'normal' : 'pointer')};
  text-align: center;
  pointer-events: ${(props) => props.disabled && 'none'};
  font-size: 19px;
  font-weight: 600;

  &:hover {
    background-color: ${(props) =>
      props.disabled
        ? props.theme.connectExistingGiftCardButtonBackgroundColor
        : props.theme.connectExistingGiftCardButtonHoverColor};
    color: ${(props) =>
      props.disabled
        ? props.theme.gray
        : props.theme.connectExistingGiftCardButtonTextHoverColor};
    transition: all 0.25s ease;
  }
`;

const Button = styled(PrimaryButton)`
  height: 56px;
  width: 392px;
  border-radius: ${(props) => props.theme.giftCardButtonRadius};
`;

const FundBox = styled.div`
  cursor: pointer;
  height: 48px;
  width: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.03px;
  color: ${(props) =>
    props.isSelected
      ? props.theme.tipBoxSelectedTextColour
      : props.theme.tipBoxTextColour};
  background-color: ${(props) =>
    props.isSelected
      ? props.theme.tipSelectedBackgroundColor
      : props.theme.tipBackgroundColor};
  font-family: ${(props) => props.theme.primaryFontFontFamily};

  &:hover {
    background-color: ${(props) =>
      props.isSelected
        ? props.theme.tipBoxSelectedColorHover
        : props.theme.tipBoxColorHover};
    color: ${(props) =>
      props.isSelected && props.theme.tipBoxSelectedTextHover};
    transition: all 0.25s ease;
  }

  @media (max-width: 786px) {
    width: 60px;
  }
`;

const CardNumberDiv = styled.div`
  border-bottom: 1px solid #c1c1c1;
  padding-bottom: '10px';
`;

const CustomFundInput = styled.input`
  width: 80px;
  border: 1px solid #d8d8d8;
  background: inherit;
  border-radius: 8px;
  text-align: center;

  &:focus {
    outline: none;
  }

  @media (max-width: 786px) {
    width: 50px;
  }
`;

const Delete = styled(Headline)`
  color: ${(props) =>
    props.disabled
      ? props.theme.primaryButtonDisabledColor
      : props.theme.black};
  align-self: center;
  cursor: pointer;
  font-weight: normal;
`;

const GiftCardImage = styled.img`
  width: 253px;
  height: 168px;
  border-radius: 16px;
`;

const fundAmtList = [10, 20, 35];

const ReloadGiftCard = ({
  giftCardDetails,
  handleReloadFunds,
  handleDefaultPaymentMethod,
  handleRemoveGiftCard,
  requestingDeletePaymentMethod,
  sourceConfig,
  showConfirmDeleteModal,
  hideConfirmDeleteModal,
  confirmDeleteGiftCardIsVisible,
  defaultPaymentMethod,
}) => {
  const [initialValues, setInitialValues] = useState({});
  const [currentPaymentMethodId, setCurrentPaymentMethod] = useState(0);
  useEffect(() => {
    if (defaultPaymentMethod?.id !== currentPaymentMethodId) {
      setInitialValues({
        fundAmt: '',
        giftCardDetails: giftCardDetails,
        cvv: sourceConfig.gift_card?.payment_cvv_required ? '' : undefined,
      });
      setCurrentPaymentMethod(defaultPaymentMethod?.id);
    }
  }, [
    defaultPaymentMethod?.id,
    sourceConfig.gift_card?.payment_cvv_required,
    currentPaymentMethodId,
  ]);

  const {
    gift_card_reload_enabled: isGiftCardReloadEnabled,
    gift_card,
  } = sourceConfig;
  const [isReloadingCard, setIsReloadingCard] = useState(
    appConfig?.nonRemovableGiftCard ?? false
  );
  return (
    <Wrapper>
      <div style={{ textAlign: 'center' }}>
        <GiftCardImage src={appConfig.styleConfig.giftCardImage} />
      </div>
      <CardDetailsWrapper>
        <TitleWrapper id='ReloadGiftCard-Title'>Card Number</TitleWrapper>
        <CardNumberDiv>
          {`${appConfig.giftCardName}  ******`}
          {giftCardDetails.details.last_four}
        </CardNumberDiv>
      </CardDetailsWrapper>
      <ContentWrapper>
        <div>
          <TitleWrapper id='ReloadGiftCard-CardBalance'>
            Card Balance
          </TitleWrapper>
          <div style={{ fontSize: '28px', fontWeight: '600' }}>
            ${giftCardDetails.details.balance}
          </div>
        </div>
        <div>
          {!isReloadingCard && isGiftCardReloadEnabled && (
            <PrimaryButtonHeadline
              id='ReloadGiftCard-AddFunds'
              type='submit'
              onClick={() => setIsReloadingCard(true)}
              style={{
                minWidth: '232px',
                borderRadius: '10px',
                height: '56px',
                fontWeight: 'normal',
              }}
            >
              Add Funds
            </PrimaryButtonHeadline>
          )}
        </div>
      </ContentWrapper>
      {isReloadingCard && (
        <div>
          <TitleWrapper id='ReloadGiftCard-CardBalance'>Add Funds</TitleWrapper>
          <Formik
            initialValues={initialValues}
            validate={(values) => {
              let errors = {};
              if (!values.fundAmt) {
                errors.fundAmt = 'Required';
              } else if (values.fundAmt < 5) {
                errors.fundAmt = 'Cannot be less than $5';
              }
              if (!values.cvv && gift_card?.payment_cvv_required) {
                errors.cvv = 'Required';
              }
              if (values?.cvv?.length > 4) {
                errors.cvv = 'CVV must be less than 4 digits';
              }
              return errors;
            }}
            validateOnBlur={false}
            onSubmit={(values) => {
              handleReloadFunds(values);
            }}
            enableReinitialize
          >
            {({
              values,
              errors,
              touched,
              handleSubmit,
              setFieldValue,
              handleChange,
              isValid,
              dirty,
            }) => (
              <form onSubmit={handleSubmit}>
                <FundWrapper>
                  {fundAmtList.map((fundAmt) => {
                    const isSelected = fundAmt == values.fundAmt;
                    return (
                      <FundBox
                        id='ReloadGiftCard-FundBox'
                        onClick={() => {
                          setFieldValue('fundAmt', fundAmt);
                        }}
                        isSelected={isSelected}
                      >
                        ${fundAmt}
                      </FundBox>
                    );
                  })}
                  <CustomFundInput
                    id='ReloadGiftCard-FundBox'
                    onKeyDown={(evt) => evt.key === 'e' && evt.preventDefault()}
                    value={values.fundAmt === -1 ? '' : values.fundAmt}
                    onChange={(e) => {
                      setFieldValue('fundAmt', e.target.value);
                    }}
                    error={touched.fundAmt && errors.fundAmt ? true : false}
                    helperText={touched.fundAmt && errors.fundAmt}
                    type='number'
                    placeholder='Other'
                    min='5'
                    maxLength='3'
                  />
                </FundWrapper>
                <CartPaymentMethodContainer isForGiftCard={true} />
                {gift_card?.payment_cvv_required && (
                  <TextField
                    id='ReloadGiftCard-CVV'
                    variant='outlined'
                    name='cvv'
                    value={values.cvv}
                    onKeyDown={(e) => {
                      if (
                        e.key !== 'Backspace' &&
                        e.key !== 'Tab' &&
                        e.key !== 'Delete' &&
                        e.key !== 'ArrowLeft' &&
                        e.key !== 'ArrowRight' &&
                        (e.key < '0' || e.key > '9')
                      ) {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      handleChange(e.target.value);
                      setInitialValues({ ...values, cvv: e.target.value });
                    }}
                    label='CVV'
                    placeholder='CVV'
                    required
                    fullWidth
                    style={{ marginBottom: '20px' }}
                    error={errors.cvv}
                    helperText={errors.cvv}
                    maxLength={4}
                  />
                )}
                <Button
                  id='ReloadGiftCard-AddFunds'
                  type='submit'
                  disabled={
                    Object.keys(errors).length > 0 ||
                    values.fundAmt === '' ||
                    values.fundAmt === '' ||
                    defaultPaymentMethod?.type === 'gift_card'
                  }
                >
                  Add Funds
                </Button>
              </form>
            )}
          </Formik>
        </div>
      )}
      {!isReloadingCard && (
        <Delete
          id='ReloadGiftCard-RemoveCard'
          onClick={() => showConfirmDeleteModal()}
          disabled={requestingDeletePaymentMethod}
        >
          <i className='fas fa-trash-alt' /> Remove Card
        </Delete>
      )}
      {!isReloadingCard && (
        <MakeDefaultButton
          id='ReloadGiftCard-MakeDefault'
          disabled={giftCardDetails.is_preferred}
          onClick={() => {
            handleDefaultPaymentMethod(giftCardDetails.id);
          }}
        >
          Make Default Payment Method
        </MakeDefaultButton>
      )}

      <ConfirmDelete
        id='ReloadGiftCard-ConfirmDelete'
        handleCancelDeletePaymentMethod={hideConfirmDeleteModal}
        confirmDeletePaymentMethodIsVisible={confirmDeleteGiftCardIsVisible}
        requestingDeletePaymentMethod={requestingDeletePaymentMethod}
        handleDeletePaymentMethod={() => {
          handleRemoveGiftCard(giftCardDetails.id);
        }}
      />
    </Wrapper>
  );
};

export default ReloadGiftCard;
